import { Pencil, Trash2, UserPlus, Clock, Share2 } from "lucide-react";
import { TagChip } from "@/components/quotes/TagInput";
import { eventLabel } from "@/components/quotes/EventPicker";
import { catColor } from "@/lib/lore";

const Speaker = ({ ln, onNavigate }) => (ln.entity
  ? <button onClick={() => onNavigate(ln.entity.id)} className="font-medium underline decoration-dotted underline-offset-4 hover:text-[#F59E0B]" style={{ textDecorationColor: catColor(ln.entity.category) }} data-testid={`quote-speaker-link-${ln.entity.id}`}>{ln.entity.name}</button>
  : <span className="font-medium text-[#A8A090]">{ln.speaker_name || "Unattributed"}{ln.speaker_name && <span className="ml-1 text-[10px] uppercase tracking-wider">(unlinked)</span>}</span>);

export const QuoteCard = ({ q, onNavigate, canEdit, onEdit, onShare, onAssign, onDelete, highlight, onTag, activeTags = [] }) => {
  const multi = q.lines.length > 1;
  return (
    <article className="group rounded-md border border-[#2D3442] bg-[#13171F] p-5 transition-colors hover:border-[#D97706]/40" data-testid={`quote-card-${q.id}`}>
      {multi && <div className="lf-label mb-3">Conversation</div>}
      <div className="space-y-2.5 border-l-2 border-[#D97706]/60 pl-4">
        {q.lines.map((ln, i) => (
          <p key={i} className={`font-display leading-snug text-[#F3EBDD] ${multi ? "text-lg" : "text-2xl italic"} ${highlight && ln.entity_id === highlight ? "text-[#F59E0B]" : ""}`}>
            {multi && <span className="mr-2 font-sans text-sm not-italic"><Speaker ln={ln} onNavigate={onNavigate} />:</span>}
            {multi ? ln.text : `“${ln.text}”`}
          </p>
        ))}
      </div>
      {(q.events?.length > 0 || q.tags?.length > 0) && (
        <div className="mt-3 space-y-1.5">
          {q.events?.map((e) => (
            <div key={e.id} className="flex items-center gap-1.5 text-xs text-[#C4B5FD]" data-testid={`quote-event-${q.id}-${e.id}`}>
              <Clock className="h-3 w-3 shrink-0" /><span className="truncate">at {e.title}</span><span className="shrink-0 text-[#A8A090]">· {eventLabel(e)}</span>
            </div>
          ))}
          {q.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {q.tags.map((t) => <TagChip key={t} tag={t} active={activeTags.includes(t)} onClick={onTag ? () => onTag(t) : undefined} testid={`quote-tag-${q.id}-${t}`} />)}
            </div>
          )}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#E8DFD1]">
        {!multi && <span>— <Speaker ln={q.lines[0]} onNavigate={onNavigate} /></span>}
        {q.context && <span className="text-xs italic text-[#A8A090]" data-testid={`quote-context-${q.id}`}>{q.context}</span>}
        {q.unattributed && <span className="rounded-sm border border-[#F59E0B]/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[#F59E0B]" data-testid={`quote-unattributed-${q.id}`}>Unattributed</span>}
        {canEdit && (
          <span className="ml-auto flex gap-1 sm:opacity-0 sm:group-hover:opacity-100">
            {onShare && <button onClick={() => onShare(q)} className="flex h-8 items-center gap-1 rounded px-2 text-xs text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F59E0B]" aria-label="Share as image" data-testid={`quote-share-${q.id}`}><Share2 className="h-3.5 w-3.5" />Share</button>}
            {q.unattributed && <button onClick={() => onAssign(q)} className="flex h-8 items-center gap-1 rounded px-2 text-xs text-[#F59E0B] hover:bg-[#1A202C]" data-testid={`quote-assign-${q.id}`}><UserPlus className="h-3.5 w-3.5" />Assign</button>}
            <button onClick={() => onEdit(q)} className="flex h-8 w-8 items-center justify-center rounded text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" aria-label="Edit quote" data-testid={`quote-edit-${q.id}`}><Pencil className="h-3.5 w-3.5" /></button>
            <button onClick={() => onDelete(q)} className="flex h-8 w-8 items-center justify-center rounded text-[#A8A090] hover:bg-[#1A202C] hover:text-red-400" aria-label="Delete quote" data-testid={`quote-delete-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        )}
      </div>
    </article>
  );
};
